// tslint:disable:no-console
import { getCurrentPage } from "@kaggle/core";
import { analytics } from "@kaggle/protos";
import jwt_decode from "jwt-decode";
import { v4 as uuid } from "uuid";
import { getSessionClaims } from "../sessionClaims";
import { EventCategory } from "./EventCategory";
import { EventType } from "./EventType";
import {
  consoleFlushEventQueue,
  EventQueueFlush,
  prodFlushEventQueue,
  SessionInfo,
} from "./flushEventQueue";

// We test that this isn't "production" to allow tests to run with the debug client (isn't specified in tests).
const IS_DEV = process.env.NODE_ENV !== "production";

// TODO(cochamberlain): This should never being called here. The caller of makeLogger must be refactored.
const IS_JUPYTER =
  location.origin &&
  (location.origin.includes("kkb-") ||
    location.origin.includes("kaggleusercontent.com"));

/** Use to stub out things on both development and Jupyter frame. */
const USE_STUB = IS_DEV || IS_JUPYTER;

/** A Logger instance is used to write analytics events to an output stream. */
export interface Logger {
  /**
   * Logs an event.
   *
   * @param category Category of event to be logged.
   * @param type Type of event to be logged.
   * @param payload Additional information about event. Should be object (not primitive) of arbitrary structure.
   */
  report: (category: EventCategory, type: EventType, payload?: object) => void;

  /**
   * Logs an event immediately (when page is about to unload).
   *
   * @param category Category of event to be logged.
   * @param type Type of event to be logged.
   * @param payload Additional information about event. Should be object (not primitive) of arbitrary structure.
   */
  reportImmediate: (
    category: EventCategory,
    type: EventType,
    payload?: object
  ) => Promise<void>;

  /**
   * Handles any teardown required by the logger or output stream.
   *
   * Common teardown operations are flushing any pending events or clearing
   * any active intervals set with `setInterval`.
   */
  close: () => void;
}

/** Interval between uploading queued events to analytics service in millis. */
const PUBLISH_EVENT_INTERVAL_MS = 15000; // 15 seconds.

/** Type alias used to improve code readability. */
type KaggleEvent = analytics.kaggle.analytics.ClientAnalyticsEvent;

// Coalesce the userId from JWT token claims / fallback to window.Kaggle.Current.userId.
const sessionClaims = getSessionClaims();
let userId = sessionClaims ? sessionClaims.uid : undefined;
if (!userId) {
  // TODO(cochamberlain): Convert to optional property chains.
  const globalKaggle = (window as any).Kaggle;
  const globalCurrent = globalKaggle ? globalKaggle.Current : undefined;
  userId = globalCurrent ? globalCurrent.userId : undefined;
}

const pageview = uuid();

const sessionInfo: SessionInfo = {
  userId,
  pageview,
};

/**
 * Helper function used to create a `Logger` instance.
 *
 * @param flushEventQueue Method used to periodically process events
 *      in event queue.
 * @param fetchToken Function used to fetch the analytics token to be used.
 */
const createLogger = (
  flushEventQueue: EventQueueFlush,
  fetchToken: () => Promise<string>
): Logger => {
  const queue: KaggleEvent[] = [];

  let token: string | null = null;

  let timeoutId: number;
  /** Cancel polling reporters flush. */
  const cancelPoll = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  };

  /** Check if the token exists and whether it has already expired. */
  const hasValidToken = () => {
    if (token === null) {
      return false;
    }

    // If we are using stubs, the token won't be something valid anyways so skip expiration check.
    if (USE_STUB) {
      return true;
    }

    // To prevent unnecessary back and forth we will try to detect if the JWT token is valid or expired (15 minute expiry).
    // To prevent issues, odd cases will warn on console and pretend things are good, allowing old logic to handle.
    try {
      const decoded = jwt_decode(token) as any;
      if (!decoded) {
        console.warn("Could not decode analytics token. Ignoring...");
        return true;
      }
      if (!decoded.exp) {
        console.warn(
          "Decoded analytics token contained no exp property. Ignoring..."
        );
        return true;
      }
      // Check if exp claim is in the future.
      if (decoded.exp < (new Date().getTime() + 1) / 1000) {
        return false;
      }
    } catch (err) {
      console.warn(
        "An error occurred decoding analytics token. Ignoring...\n",
        err.message
      );
    }
    return true;
  };

  /** Calling this continually flushes events and polling interval until cancelPoll is called. */
  const flushEventsPoll = async () => {
    cancelPoll();

    if (queue.length === 0) {
      // Return early if queue is empty. Avoid refreshing the token in this case as it's
      // not going to be used anyways.
      timeoutId = window.setTimeout(flushEventsPoll, PUBLISH_EVENT_INTERVAL_MS);
      return;
    }

    if (!hasValidToken()) {
      token = getAnalyticsTokenFromLocalStorage();
      if (!hasValidToken()) {
        token = await fetchToken();
        saveAnalyticsTokenToLocalStorage(token);
      }
    }

    if (token !== null) {
      // Flush events in queue if session metadata is set.
      const events = queue.splice(0);
      const status = await flushEventQueue(events, sessionInfo, token);

      if (status === 401) {
        // If received unauthorized, token may have expired. Re-fetch token on next round.
        token = null;
      }
    }
    timeoutId = window.setTimeout(flushEventsPoll, PUBLISH_EVENT_INTERVAL_MS);
  };

  // Schedule event flushing to start polling at correct interval.
  timeoutId = window.setTimeout(flushEventsPoll, PUBLISH_EVENT_INTERVAL_MS);

  /** Report an event. Not flushed immediately. */
  const report = (
    category: EventCategory,
    type: EventType,
    payload?: object
  ) => {
    queue.push(
      analytics.kaggle.analytics.ClientAnalyticsEvent.create({
        eventCategory: category,
        eventType: type,
        eventTime: new Date().toISOString(),
        eventDataJson: JSON.stringify(payload || {}),
        eventPage: getCurrentPage().pageType,
      })
    );
  };

  /** Report events immediately (when page is about to unload). */
  const reportImmediate = async (
    category: EventCategory,
    type: EventType,
    payload?: object
  ) => {
    cancelPoll();
    report(category, type, payload);
    await flushEventsPoll();
  };

  return {
    report,
    reportImmediate,
    close: () => {
      cancelPoll();
    },
  };
};

class AnalyticsToken {
  constructor(value: string) {
    this.value = value;
    this.refreshTime = new Date().getTime();
  }

  value: string;
  refreshTime: number;
}

// This is shared with "Kaggle.Web/Views/Shared/Analytics/AnalyticsReporter.ts".
const ANALYTICS_TOKEN_KEY = "analyticsToken";

const getAnalyticsTokenFromLocalStorage = () => {
  try {
    const tokenJson = localStorage.getItem(ANALYTICS_TOKEN_KEY);
    if (!tokenJson) {
      return null;
    }
    const storedToken: AnalyticsToken = JSON.parse(tokenJson);
    return storedToken.value;
  } catch {
    return null;
  }
};

const saveAnalyticsTokenToLocalStorage = (token: string) => {
  try {
    localStorage.setItem(
      ANALYTICS_TOKEN_KEY,
      JSON.stringify(new AnalyticsToken(token))
    );
  } catch {
    // no-op
  }
};

const getAnalyticsToken = async () => {
  if (USE_STUB) {
    return "DEV_TOKEN";
  }
  const response = await fetch(`${location.origin}/analytics/token`, {
    method: "GET",
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
    referrer: "no-referrer",
  });
  const result = await response.json();
  return result.token;
};

// TODO(cochamberlain): Change filename from makeLogger.ts to logger.ts
/** Singleton logger, ensures only one instance exists. */
export const logger = createLogger(
  USE_STUB ? consoleFlushEventQueue : prodFlushEventQueue,
  getAnalyticsToken
);

export const reportEvent = logger.report;
